import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../auth/AuthContext";
import { api } from "../../api/clientInstance";
import { apiErrorMessage } from "../../utils/apiError";
import Modal from "../../components/Modal";
import { Loader, Plus, LogIn } from "lucide-react";
import "./CupsHubPage.css";

// A cup needs at least 4 participants (mirror of backend MIN_CUP_MEMBERS);
// for a friend cup that's the organizer + 3 invited friends. Max is 10
// participants (MAX_CUP_MEMBERS) → up to 9 invited friends.
const MIN_FRIENDS = 3;
const MAX_FRIENDS = 9;

// Per-organizer cup limits (mirror backend limits.py). Removed cups don't count.
const MAX_OPEN_CUPS   = 4;
const MAX_FRIEND_CUPS = 1;

// Cup.name column is VARCHAR(100) (backend models/cup.py) — cap input to match.
const CUP_NAME_MAX = 100;

const FIELD_BG = `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(${process.env.PUBLIC_URL}/images/field_bg.webp)`;

// Cups hub: the single entry point for everything cup-related.
// Lists every cup you're in (owned or joined) as previews; Join/Create open
// modals. Clicking a preview opens /cup/:id, the role/status-aware cup page.
export default function CupsHubPage() {
  const { t }    = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [cups,    setCups]    = useState([]);
  const [friends, setFriends] = useState([]);

  // modal: null | "join" | "create". In "create", cupType null = choose type.
  const [modal,   setModal]   = useState(null);
  const [cupType, setCupType] = useState(null);

  const [newCupName, setNewCupName]           = useState("");
  const [participate, setParticipate]         = useState(true);
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [creating, setCreating]               = useState(false);
  const [createError, setCreateError]         = useState("");
  const [joinCode, setJoinCode]               = useState("");
  const [joining, setJoining]                 = useState(false);
  const [joinError, setJoinError]             = useState("");

  const loadCups = useCallback(() => {
    api("/cup/mine").then(setCups).catch(() => {});
  }, []);

  useEffect(() => {
    loadCups();
    api("/friends").then(setFriends).catch(() => {});
  }, [loadCups]);

  const closeModal = () => setModal(null);

  const openJoin = () => {
    setJoinCode(""); setJoinError(""); setModal("join");
  };
  const openCreate = () => {
    setCupType(null); setNewCupName(""); setParticipate(true); setSelectedFriends([]); setCreateError(""); setModal("create");
  };

  const handleCreate = async () => {
    const name = newCupName.trim();
    if (!name) { setCreateError(t("cup.nameRequired")); return; }
    setCreating(true);
    setCreateError("");
    try {
      await api("/cup", { method: "POST", body: JSON.stringify({ name, participate }) });
      closeModal();
      loadCups();
    } catch (e) {
      setCreateError(apiErrorMessage(e, t));
    } finally {
      setCreating(false);
    }
  };

  const handleCreateFriend = async () => {
    const name = newCupName.trim();
    if (!name) { setCreateError(t("cup.nameRequired")); return; }
    if (selectedFriends.length < MIN_FRIENDS) {
      setCreateError(t("cup.minParticipants", { count: MIN_FRIENDS }));
      return;
    }
    setCreating(true);
    setCreateError("");
    try {
      await api("/cup/friend", { method: "POST", body: JSON.stringify({ name, friend_ids: selectedFriends }) });
      closeModal();
      loadCups();
    } catch (e) {
      setCreateError(apiErrorMessage(e, t));
    } finally {
      setCreating(false);
    }
  };

  const handleJoin = async () => {
    const code = joinCode.trim().toUpperCase();
    if (!code) return;
    setJoining(true);
    setJoinError("");
    try {
      await api(`/cup/join/${code}`, { method: "POST" });
      closeModal();
      loadCups();
    } catch (e) {
      setJoinError(apiErrorMessage(e, t));
    } finally {
      setJoining(false);
    }
  };

  const toggleFriend = (id) =>
    setSelectedFriends((prev) =>
      prev.includes(id) ? prev.filter((f) => f !== id)
      : prev.length >= MAX_FRIENDS ? prev            // cap at 10 participants
      : [...prev, id]);

  // Cups I organize, by type (removed cups aren't returned by /cup/mine).
  const myOpenCups   = cups.filter((c) => c.is_organizer && c.type === "school");
  const myFriendCups = cups.filter((c) => c.is_organizer && c.type === "friend");
  const atOpenLimit   = myOpenCups.length   >= MAX_OPEN_CUPS;
  const atFriendLimit = myFriendCups.length >= MAX_FRIEND_CUPS;

  return (
    <div className="cups-hub-bg" style={{ backgroundImage: FIELD_BG }}>
      <div className="cups-hub">
        <div className="cups-hub-head">
          <h2 className="cups-hub-title">{t("nav.cups")}</h2>
          <div className="cups-hub-actions">
            <button className="btn-secondary" onClick={openJoin}>
              <LogIn size={15} /> {t("cup.join")}
            </button>
            <button className="btn-primary" onClick={openCreate}>
              <Plus size={15} /> {t("cup.create")}
            </button>
          </div>
        </div>

        {/* Cup previews */}
        <div className="cup-list">
          {cups.length === 0 && <p className="cups-hub-empty">{t("cup.noCups")}</p>}
          {cups.map((c) => (
            <button key={c.id} className="cup-card" onClick={() => navigate(`/cup/${c.id}`)}>
              <span className="cup-card-name">{c.name}</span>
              <span className={`cup-row-badge cup-row-badge--${c.type}`}>
                {c.type === "school" ? t("cup.typeSchool") : t("cup.typeFriend")}
              </span>
              <span className={`cup-status-badge cup-status-badge--${c.status}`}>
                {t(`cup.status.${c.status}`)}
              </span>
              {c.join_code && <span className="cup-row-code">{c.join_code}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Join modal */}
      {modal === "join" && (
        <Modal onClose={closeModal}>
          <h3 className="cup-modal-title">{t("cup.join")}</h3>
          <div className="country-picker">
            <input
              className="country-select"
              placeholder={t("cup.joinPlaceholder")}
              value={joinCode}
              autoFocus
              onChange={(e) => { setJoinCode(e.target.value.toUpperCase()); setJoinError(""); }}
              onKeyDown={(e) => e.key === "Enter" && handleJoin()}
              maxLength={6}
              disabled={joining}
              style={{ fontFamily: "monospace", letterSpacing: "0.15em" }}
            />
            <button className="team-save-btn" onClick={handleJoin} disabled={!joinCode.trim() || joining}>
              {joining ? <Loader size={16} className="spin" /> : "→"}
            </button>
          </div>
          {joinError && <p className="profile-error">{joinError}</p>}
          <div className="cup-modal-actions">
            <button className="cup-cancel-btn" onClick={closeModal} disabled={joining}>{t("cancel")}</button>
          </div>
        </Modal>
      )}

      {/* Create modal */}
      {modal === "create" && (
        <Modal onClose={closeModal}>
          {cupType === null ? (
            <>
              <h3 className="cup-modal-title">{t("cup.chooseType")}</h3>
              <button className="cup-type-choice" onClick={() => { setCupType("school"); setCreateError(""); }}>
                <span className="cup-type-choice-name">{t("cup.typeSchool")}</span>
                <span className="cup-type-choice-desc">{t("cup.typeSchoolDesc")}</span>
              </button>
              <button className="cup-type-choice" onClick={() => { setCupType("friend"); setCreateError(""); }}>
                <span className="cup-type-choice-name">{t("cup.typeFriend")}</span>
                <span className="cup-type-choice-desc">{t("cup.typeFriendDesc")}</span>
              </button>
              <div className="cup-modal-actions">
                <button className="cup-cancel-btn" onClick={closeModal}>{t("cancel")}</button>
              </div>
            </>
          ) : (
            <>
              {cupType === "school" && (
                atOpenLimit ? (
                  <p className="profile-empty">{t("cup.openLimitReached", { count: MAX_OPEN_CUPS })}</p>
                ) : user?.email_verified ? (
                  <>
                    <div className="country-picker">
                      <input
                        className="country-select"
                        placeholder={t("cup.namePlaceholder")}
                        value={newCupName}
                        autoFocus
                        maxLength={CUP_NAME_MAX}
                        onChange={(e) => { setNewCupName(e.target.value); setCreateError(""); }}
                        onKeyDown={(e) => e.key === "Enter" && newCupName.trim() && handleCreate()}
                        disabled={creating}
                      />
                    </div>
                    <label className="cup-participate-toggle">
                      <input
                        type="checkbox"
                        checked={participate}
                        onChange={(e) => setParticipate(e.target.checked)}
                        disabled={creating}
                      />
                      {t("cup.participate")}
                    </label>
                  </>
                ) : (
                  <p className="profile-empty">{t("cup.needsVerifiedEmail")}</p>
                )
              )}

              {cupType === "friend" && (
                atFriendLimit ? (
                  <p className="profile-empty">{t("cup.friendLimitReached", { name: myFriendCups[0]?.name })}</p>
                ) : friends.length < MIN_FRIENDS ? (
                  <p className="profile-empty">{t("cup.needMoreFriends", { count: MIN_FRIENDS })}</p>
                ) : (
                  <>
                    <div className="country-picker">
                      <input
                        className="country-select"
                        placeholder={t("cup.namePlaceholder")}
                        value={newCupName}
                        autoFocus
                        maxLength={CUP_NAME_MAX}
                        onChange={(e) => { setNewCupName(e.target.value); setCreateError(""); }}
                        disabled={creating}
                      />
                    </div>
                    <p className="cup-section-label" style={{ marginTop: "1rem" }}>{t("cup.selectFriends")}</p>
                    <div className="cup-friend-picker">
                      {friends.map((f) => (
                        <div key={f.id} className="cup-friend-row" onClick={() => toggleFriend(f.id)}>
                          <span className={`cup-friend-check ${selectedFriends.includes(f.id) ? "cup-friend-check--on" : ""}`}>✓</span>
                          <span className="cup-member-name">{f.name}</span>
                        </div>
                      ))}
                    </div>
                    {selectedFriends.length < MIN_FRIENDS && (
                      <p className="profile-empty">{t("cup.minParticipants", { count: MIN_FRIENDS })}</p>
                    )}
                    {selectedFriends.length >= MAX_FRIENDS && (
                      <p className="profile-empty">{t("cup.maxParticipants", { count: MAX_FRIENDS + 1 })}</p>
                    )}
                  </>
                )
              )}

              {createError && <p className="profile-error">{createError}</p>}

              <div className="cup-modal-actions cup-modal-actions--split">
                <button className="cup-cancel-btn" onClick={closeModal} disabled={creating}>{t("cancel")}</button>
                <button
                  className="btn-primary"
                  onClick={cupType === "school" ? handleCreate : handleCreateFriend}
                  disabled={
                    creating ||
                    !newCupName.trim() ||
                    (cupType === "school" && atOpenLimit) ||
                    (cupType === "friend" && (atFriendLimit || friends.length < MIN_FRIENDS))
                  }
                >
                  {creating ? <Loader size={14} className="spin" /> : t("cup.create")}
                </button>
              </div>
            </>
          )}
        </Modal>
      )}
    </div>
  );
}
